import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';

const STATUS_CONFIG = {
  Delivered: { color: 'text-green-700 bg-green-50 border-green-200', icon: 'CheckCircle2', iconColor: '#16a34a' },
  RTO: { color: 'text-red-700 bg-red-50 border-red-200', icon: 'XCircle', iconColor: '#dc2626' },
  Pending: { color: 'text-amber-700 bg-amber-50 border-amber-200', icon: 'Clock', iconColor: '#d97706' },
  Rescheduled: { color: 'text-blue-700 bg-blue-50 border-blue-200', icon: 'CalendarClock', iconColor: '#2563eb' },
  Denied: { color: 'text-red-600 bg-red-50 border-red-200', icon: 'Ban', iconColor: '#dc2626' },
  'No Response': { color: 'text-gray-600 bg-gray-50 border-gray-200', icon: 'PhoneMissed', iconColor: '#6b7280' }
};

const NDR_REASONS = {
  'Customer Unavailable': 'Customer was not reachable at the time of delivery attempt.',
  'Wrong Address': 'Address provided was incorrect or incomplete.',
  'Refused Delivery': 'Customer refused to accept the package.',
  'Payment Not Ready': 'Customer was not ready with payment (COD).',
  'Premises Locked': 'Delivery location was locked / no one present.',
  'Rescheduled by Customer': 'Customer requested a different delivery slot.'
};

const ATTEMPT_COLORS = [
  { bg: '#ffffff', text: '#111827', border: 'rgba(0, 0, 0, 0.15)', iconColor: '#2563eb' },
  { bg: '#ffffff', text: '#111827', border: 'rgba(0, 0, 0, 0.15)', iconColor: '#7c3aed' },
  { bg: '#ffffff', text: '#111827', border: 'rgba(0, 0, 0, 0.15)', iconColor: '#d97706' }
];

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.['Pending'];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg?.color}`}>
      <Icon name={cfg?.icon} size={11} color={cfg?.iconColor} />
      {status}
    </span>
  );
};

const CallOutcomeBadge = ({ value }) => {
  const map = {
    Rescheduled: 'text-green-700 bg-green-50 border-green-200',
    Denied: 'text-red-600 bg-red-50 border-red-200',
    'No Response': 'text-gray-600 bg-gray-50 border-gray-200'
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${map?.[value] || 'text-gray-600 bg-gray-50 border-gray-200'}`}>
      {value}
    </span>
  );
};

const CallDots = ({ count }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 6 }).map((_, i) => (
      <span key={i} className={`w-2 h-2 rounded-full ${i < count ? 'bg-blue-500' : 'bg-gray-200'}`} />
    ))}
    <span className="ml-1 text-xs text-gray-500">{count}</span>
  </div>
);

const SkeletonRow = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 13 }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-4 bg-gray-100 rounded w-full" />
      </td>
    ))}
  </tr>
);

const NDRTable = ({ from, to, filters, refreshKey, onRowClick }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);
  const size = 10;

  // Debounce the filters.search value
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search || '');
      setPage(0);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [filters.search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params = { from, to, page, size };
    if (filters.ndrStatus && filters.ndrStatus !== 'all') params.status = filters.ndrStatus;
    if (filters.callOutcome && filters.callOutcome !== 'all') params.callOutcome = filters.callOutcome;
    if (debouncedSearch) params.search = debouncedSearch;

    api
      .get('/api/v1/ndr-management/orders', { params })
      .then((res) => {
        if (!cancelled) {
          const d = res.data?.data || {};
          setData(d.rows || []);
          setTotalPages(d.totalPages || 0);
          setTotalOrders(d.totalOrders || 0);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [from, to, page, filters.ndrStatus, filters.callOutcome, debouncedSearch, refreshKey]);

  // Reset page on filter change
  useEffect(() => {
    setPage(0);
  }, [filters.ndrStatus, filters.callOutcome, from, to]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // Client-side sort on current page data
  const sorted = [...data].sort((a, b) => {
    if (!sortField) return 0;
    let av, bv;
    if (sortField === 'orderValue') {
      av = parseFloat((a?.orderValue || '0').replace(/[^0-9.]/g, ''));
      bv = parseFloat((b?.orderValue || '0').replace(/[^0-9.]/g, ''));
    } else if (sortField === 'calls') {
      av = Math.max(...(a?.attempts || []).map((at) => at?.callsAttempted || 0));
      bv = Math.max(...(b?.attempts || []).map((at) => at?.callsAttempted || 0));
    } else if (sortField === 'orderDate') {
      av = a?.orderDate ? new Date(a.orderDate).getTime() : 0;
      bv = b?.orderDate ? new Date(b.orderDate).getTime() : 0;
    } else if (sortField === 'lastUpdated') {
      const lastTs = (row) => {
        const attempts = (row?.attempts || []).filter(at => at?.timestamp);
        return attempts.length > 0 ? new Date(attempts[attempts.length - 1].timestamp).getTime() : 0;
      };
      av = lastTs(a);
      bv = lastTs(b);
    }
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  const SortIcon = ({ field }) => (
    <button onClick={() => handleSort(field)} className="ml-1 opacity-50 hover:opacity-100 transition-opacity">
      <Icon name={sortField === field && sortDir === 'desc' ? 'ChevronDown' : 'ChevronUp'} size={16} />
    </button>
  );

  // Truncated pagination
  const getPages = () => {
    const pages = [];
    for (let i = 0; i < totalPages; i++) {
      if (i < 2 || i >= totalPages - 1 || (i >= page - 1 && i <= page + 1)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    return pages;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden relative">
      {/* Order count */}
      <div className="flex items-center justify-end px-4 py-3 border-b border-gray-100">
        <span className="text-xs text-gray-400">{totalOrders} orders</span>
      </div>

      {tooltip?.visible && (
        <div
          className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 max-w-xs shadow-xl pointer-events-none"
          style={{ left: tooltip.x + 12, top: tooltip.y - 8 }}
        >
          {tooltip.text}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="text-sm border-collapse" style={{ minWidth: '1350px' }}>
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-r border-gray-200 min-w-[120px]">Order ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-r border-gray-200 min-w-[110px]">
                <span className="flex items-center">Order Date <SortIcon field="orderDate" /></span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-r border-gray-200 min-w-[140px]">
                <span className="flex items-center">Last Updated <SortIcon field="lastUpdated" /></span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-r border-gray-200 min-w-[140px]">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-r border-gray-200 min-w-[140px]">Mobile</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-r border-gray-200 min-w-[110px]">
                <span className="flex items-center">Order Value <SortIcon field="orderValue" /></span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-r border-gray-200 min-w-[100px]">Payment Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[100px]">Attempt</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[160px]">NDR Reason</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[130px]">Call Outcome</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[140px]">
                <span className="flex items-center">Calls Attempted <SortIcon field="calls" /></span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[160px]">Rescheduled Slot</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[140px]">FINAL ORDER STATUS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={13} className="text-center py-20">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center">
                      <Icon name="PackageX" size={32} color="#d1d5db" />
                    </div>
                    <p className="text-gray-500 font-medium text-sm">No NDR orders found for selected filters</p>
                  </div>
                </td>
              </tr>
            ) : (
              sorted.map((row, rowIdx) =>
                (row.attempts || []).map((attempt, ai) => {
                  const isFirstAttempt = ai === 0;
                  const isLastAttempt = ai === (row.attempts || []).length - 1;
                  const isEvenRow = rowIdx % 2 === 0;
                  const rowBg = isEvenRow ? 'bg-white' : 'bg-gray-50/30';
                  const stickyBg = isEvenRow ? '#ffffff' : '#fafafa';
                  const borderBottom = isLastAttempt ? 'border-b-2 border-gray-200' : 'border-b border-gray-100';
                  const attemptCount = (row.attempts || []).length;

                  return (
                    <tr
                      key={`${row.orderId}-attempt-${ai}`}
                      onClick={() => onRowClick?.(row)}
                      className={`${borderBottom} cursor-pointer transition-colors hover:bg-blue-50/40 ${rowBg}`}
                    >
                      {isFirstAttempt && (
                        <>
                          <td rowSpan={attemptCount} className="px-4 py-3 border-r border-gray-100 align-middle" style={{ backgroundColor: stickyBg }}>
                            <span className="font-mono text-xs font-semibold text-blue-700">{row.orderId}</span>
                          </td>
                          <td rowSpan={attemptCount} className="px-4 py-3 border-r border-gray-100 align-middle" style={{ backgroundColor: stickyBg }}>
                            <span className="text-xs text-gray-600 whitespace-nowrap">{row.orderDate || '—'}</span>
                          </td>
                          <td rowSpan={attemptCount} className="px-4 py-3 border-r border-gray-100 align-middle" style={{ backgroundColor: stickyBg }}>
                            {(() => {
                              const lastAttempt = (row.attempts || []).filter(a => a.timestamp).slice(-1)[0];
                              if (!lastAttempt?.timestamp) return <span className="text-xs text-gray-600">—</span>;
                              const parts = lastAttempt.timestamp.split(',');
                              return (
                                <div className="text-xs text-gray-600 whitespace-nowrap">
                                  <div>{parts[0]?.trim()}</div>
                                  {parts[1] && <div className="text-gray-400">{parts[1]?.trim()}</div>}
                                </div>
                              );
                            })()}
                          </td>
                          <td rowSpan={attemptCount} className="px-4 py-3 border-r border-gray-100 align-middle" style={{ backgroundColor: stickyBg }}>
                            <span className="font-medium text-gray-800 text-xs">{row.customerName}</span>
                          </td>
                          <td rowSpan={attemptCount} className="px-4 py-3 border-r border-gray-100 align-middle" style={{ backgroundColor: stickyBg }}>
                            <span className="text-xs text-blue-600 font-medium">{row.phone}</span>
                          </td>
                          <td rowSpan={attemptCount} className="px-4 py-3 border-r border-gray-100 align-middle" style={{ backgroundColor: stickyBg }}>
                            <span className="text-xs font-semibold text-gray-800">{row.orderValue}</span>
                          </td>
                          <td rowSpan={attemptCount} className="px-4 py-3 border-r border-gray-100 align-middle" style={{ backgroundColor: stickyBg }}>
                            {row.paymentType ? (
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${
                                row.paymentType === 'COD'
                                  ? 'text-amber-700 bg-amber-50 border-amber-200'
                                  : 'text-green-700 bg-green-50 border-green-200'
                              }`}>
                                {row.paymentType}
                              </span>
                            ) : (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </td>
                        </>
                      )}

                      {/* Attempt label */}
                      <td className="px-4 py-2.5">
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                          style={{
                            backgroundColor: ATTEMPT_COLORS[ai % 3]?.bg,
                            color: ATTEMPT_COLORS[ai % 3]?.text,
                            border: `1px solid ${ATTEMPT_COLORS[ai % 3]?.border}`
                          }}
                        >
                          <Icon name="Package" size={10} color={ATTEMPT_COLORS[ai % 3]?.iconColor} />
                          Attempt {attempt.attemptNumber || ai + 1}
                        </span>
                      </td>

                      {/* NDR Reason */}
                      <td className="px-4 py-2.5">
                        {attempt.ndrReason ? (
                          <span
                            className="text-xs text-gray-700 cursor-help underline decoration-dotted"
                            onMouseEnter={(e) => {
                              const rect = e.target.getBoundingClientRect();
                              setTooltip({ visible: true, text: NDR_REASONS[attempt.ndrReason] || attempt.ndrReason, x: rect.left, y: rect.top });
                            }}
                            onMouseLeave={() => setTooltip({ visible: false, text: '', x: 0, y: 0 })}
                          >
                            {attempt.ndrReason}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>

                      {/* Call Outcome */}
                      <td className="px-4 py-2.5">
                        {attempt.callOutcome ? <CallOutcomeBadge value={attempt.callOutcome} /> : <span className="text-gray-300 text-xs">—</span>}
                      </td>

                      {/* Calls Attempted */}
                      <td className="px-4 py-2.5">
                        {attempt.callsAttempted > 0 ? <CallDots count={attempt.callsAttempted} /> : <span className="text-gray-300 text-xs">—</span>}
                      </td>

                      {/* Rescheduled Slot */}
                      <td className="px-4 py-2.5">
                        {attempt.rescheduledSlot ? (
                          <span className="text-xs text-gray-600">{attempt.rescheduledSlot}</span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-2.5">
                        {attempt.status ? <StatusBadge status={attempt.status} /> : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                    </tr>
                  );
                })
              )
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <span className="text-xs text-gray-400">
            Page {page + 1} of {totalPages} ({totalOrders} orders)
          </span>
          <div className="flex items-center gap-1">
            <button
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-30"
            >
              Prev
            </button>
            {getPages().map((p, i) =>
              p === '...' ? (
                <span key={`dots-${i}`} className="px-2 py-1 text-xs text-gray-400">...</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`px-2.5 py-1 rounded text-xs font-medium ${
                    p === page ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {p + 1}
                </button>
              )
            )}
            <button
              disabled={page >= totalPages - 1}
              onClick={() => setPage(page + 1)}
              className="px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-30"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NDRTable;
